import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Services from '../services/Services';
import { getPlatformInfo, getFullUrl, getUri } from '../utils/platformDetection';
import { useAuth } from '../contexts';

const HEARTBEAT_INTERVAL = 15000;
const SESSION_STORAGE_KEY = 'togather_analytics_session_id';

const getStoredSessionId = (): string | null => {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(SESSION_STORAGE_KEY) : null;
  } catch {
    return null;
  }
};

const setStoredSessionId = (sessionId: string): void => {
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  } catch {
    // ignore
  }
};

/**
 * Hook for tracking Togather analytics sessions.
 *
 * - Refresh: sessionID sessionStorage'da kalır, aynı session devam eder
 * - Tab kapatıp yeni tabda açılınca sessionStorage temiz olduğu için yeni session oluşur
 * - Her route değişiminde create-session isteği aynı sessionID ile tekrar gönderilir
 * - Works for unauthenticated users (uses apiToken as fallback)
 * - Heartbeat reads current URL dynamically on each tick (15s)
 */
export const useTogatherAnalytics = () => {
  const location = useLocation();
  const { token, apiToken, isLoading } = useAuth();
  const sessionIDRef = useRef<string | null>(getStoredSessionId());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionCreatedRef = useRef<boolean>(false);
  const startHeartbeatRef = useRef<(() => void) | null>(null);
  const prevPathnameRef = useRef<string | null>(null);

  // Use a ref for the auth token so the interval callback always has the latest value
  const authTokenRef = useRef<string>('');
  authTokenRef.current = token || apiToken || '';

  // ── Create session veya refresh sonrası mevcut session ile heartbeat başlat ──
  useEffect(() => {
    if (isLoading) return;
    if (sessionCreatedRef.current) return;
    sessionCreatedRef.current = true;

    const startHeartbeat = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      intervalRef.current = setInterval(async () => {
        if (!sessionIDRef.current) return;
        const url = getFullUrl();
        const uri = getUri();
        try {
          await Services.SetUserOnlineInTogather(authTokenRef.current, {
            sessionID: sessionIDRef.current,
            url,
            uri,
          });
        } catch {
          // Heartbeat failed — ignore
        }
      }, HEARTBEAT_INTERVAL);
    };
    startHeartbeatRef.current = startHeartbeat;

    // Refresh: sessionStorage'dan sessionID varsa sadece heartbeat başlat (route effect mevcut session ile güncelleyecek)
    if (sessionIDRef.current) {
      startHeartbeat();
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }

    const createSession = async () => {
      const platformInfo = getPlatformInfo();

      try {
        const response = await Services.CreateTogatherSession(authTokenRef.current, {
          platform: platformInfo.platform,
          appVersion: platformInfo.appVersion,
          isMobile: platformInfo.isMobile,
          agent: platformInfo.agent,
          url: platformInfo.url,
          uri: platformInfo.uri,
        });

        if (response.data.status && response.data.result?.sessionID) {
          const sid = response.data.result.sessionID;
          sessionIDRef.current = sid;
          setStoredSessionId(sid);
          startHeartbeat();
        }
      } catch {
        // Session creation failed — heartbeat not started
      }
    };

    createSession();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // ── Her route değişiminde create-session isteğini aynı session id ile tekrar gönder (ilk mount'ta atla, ilk session first effect'te oluşur) ──
  useEffect(() => {
    if (isLoading) return;

    const pathname = location.pathname;
    const prevPathname = prevPathnameRef.current;

    // İlk mount: sadece pathname kaydet, create-session gönderme (first effect hallediyor)
    if (prevPathname === null) {
      prevPathnameRef.current = pathname;
      return;
    }
    // Pathname değişmediyse bir şey yapma
    if (prevPathname === pathname) return;

    prevPathnameRef.current = pathname;

    const platformInfo = getPlatformInfo();
    const url = getFullUrl();
    const uri = getUri();

    const sendCreateSession = async () => {
      try {
        const payload = {
          platform: platformInfo.platform,
          appVersion: platformInfo.appVersion,
          isMobile: platformInfo.isMobile,
          agent: platformInfo.agent,
          url,
          uri,
          ...(sessionIDRef.current && { sessionID: sessionIDRef.current }),
        };
        const response = await Services.CreateTogatherSession(authTokenRef.current, payload);
        if (response.data.status && response.data.result?.sessionID && !sessionIDRef.current) {
          const sid = response.data.result.sessionID;
          sessionIDRef.current = sid;
          setStoredSessionId(sid);
          startHeartbeatRef.current?.();
        }
      } catch {
        // Create session / route update failed — ignore
      }
    };

    sendCreateSession();
  }, [location.pathname, isLoading]);

  // ── Cleanup on unmount ──
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);
};
